import { useState } from 'react';
import { Container, Title, Text, Group, Stack, Badge, SimpleGrid, Loader, Center } from '@mantine/core';
import {
  IconBox,
  IconUsers,
  IconMapPin,
  IconCategory,
  IconCurrencyDollar,
  IconCalendarStats,
  IconTrendingDown,
  IconReceipt,
} from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/hooks/useAuth';
import { useAssets } from '@/hooks/api/useAssets';
import { usePeople } from '@/hooks/api/usePeople';
import { useLocations } from '@/hooks/api/useLocations';
import { useCategories } from '@/hooks/api/useCategories';
import { useSites } from '@/hooks/api/useSites';
import { useAuditEvents } from '@/hooks/api/useAudits';
import { useCustomRoles } from '@/hooks/api/useCustomRoles';
import { useOrganizationMembers } from '@/hooks/api/useOrganizationMembers';
import { StatCard, formatCurrency } from './components/StatCard';
import { AssetStatusChart } from './components/AssetStatusChart';
import { AssetsByCategoryChart } from './components/AssetsByCategoryChart';
import { AssetValueChart } from './components/AssetValueChart';
import { RecentAssets } from './components/RecentAssets';
import { ActivityFeed } from './components/ActivityFeed';
import { AuditCalendar } from './components/AuditCalendar';
import { OnboardingChecklist } from './components/OnboardingChecklist';
import { SavedViewsCard } from './components/SavedViewsCard';
import { DashboardFilters, type DashboardFilterValues } from './components/DashboardFilters';
import { Top5Widget } from './components/Top5Widget';
import { useDashboardMetrics } from './hooks/useDashboardMetrics';
import { useMemo } from 'react';

export function DashboardPage() {
  const { user } = useAuth();
  const { organization, membership, organizationId } = useOrganization();
  const { data: customRoles = [] } = useCustomRoles(organizationId || '');

  // Filter state
  const [filters, setFilters] = useState<DashboardFilterValues>({
    siteId: null,
    personId: null,
    categoryId: null,
  });

  // Get role display name
  const roleDisplay = useMemo(() => {
    if (!membership) return '';
    if (membership.is_admin) return 'Admin';
    if (membership.custom_role_id) {
      const role = customRoles.find(r => r.id === membership.custom_role_id);
      return role?.name || 'Member';
    }
    return membership.role?.charAt(0).toUpperCase() + membership.role?.slice(1) || 'Member';
  }, [membership, customRoles]);

  // Fetch data
  const { data: assets = [], isLoading: assetsLoading } = useAssets(organizationId);
  const { data: people = [], isLoading: peopleLoading } = usePeople(organizationId);
  const { data: locations = [], isLoading: locationsLoading } = useLocations(organizationId);
  const { data: categories = [], isLoading: categoriesLoading } = useCategories(organizationId);
  const { data: sites = [], isLoading: sitesLoading } = useSites(organizationId);
  const { data: audits = [], isLoading: auditsLoading } = useAuditEvents(organizationId);
  const { data: members = [] } = useOrganizationMembers(organizationId || '');

  // Calculate metrics based on filters
  const metrics = useDashboardMetrics(assets, sites, people, categories, filters);

  const isLoading = assetsLoading || peopleLoading || locationsLoading || categoriesLoading || sitesLoading || auditsLoading;

  const hasActiveFilters = filters.siteId || filters.personId || filters.categoryId;

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container fluid>
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Group justify="apart" mb="xs">
            <Title order={1}>Dashboard</Title>
            <Badge size="lg" variant="light" color={membership?.is_admin ? 'red' : 'blue'}>
              {roleDisplay.toUpperCase()}
            </Badge>
          </Group>
          <Text c="dimmed">
            Welcome back, {user?.user_metadata?.full_name || user?.email}!
          </Text>
          {organization && (
            <Text c="dimmed" size="sm">
              Organization: {organization.name}
            </Text>
          )}
        </div>

        {/* Filters */}
        <DashboardFilters
          sites={sites}
          people={people}
          categories={categories}
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* Onboarding Checklist - shows for new users, hide when filtering */}
        {!hasActiveFilters && (
          <OnboardingChecklist
            assetCount={assets.length}
            locationCount={locations.length}
            peopleCount={people.length}
            userCount={members.length}
          />
        )}

        {/* Saved Views - shows views configured to appear on dashboard, hide when filtering */}
        {!hasActiveFilters && <SavedViewsCard />}

        {/* Primary Statistics Cards */}
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
          <StatCard
            icon={<IconBox size={24} />}
            value={metrics.totalAssets}
            label="Total Assets"
            color="blue"
            subtitle={hasActiveFilters ? `of ${assets.length} total` : undefined}
          />
          <StatCard
            icon={<IconCurrencyDollar size={24} />}
            value={formatCurrency(metrics.totalValue)}
            label="Total Asset Value"
            color="green"
          />
          <StatCard
            icon={<IconCalendarStats size={24} />}
            value={metrics.purchasedThisFiscalYear}
            label="Purchased This FY"
            color="teal"
            subtitle={formatCurrency(metrics.purchasedThisFiscalYearValue)}
          />
          <StatCard
            icon={<IconTrendingDown size={24} />}
            value={metrics.depreciableAssets}
            label="Depreciating Assets"
            color="orange"
            subtitle={`${formatCurrency(metrics.totalDepreciation)} depreciated`}
          />
        </SimpleGrid>

        {/* Secondary Statistics Cards - counts only */}
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
          <StatCard
            icon={<IconUsers size={24} />}
            value={people.length}
            label="People"
            color="violet"
          />
          <StatCard
            icon={<IconMapPin size={24} />}
            value={sites.length}
            label="Primary Locations"
            color="grape"
            subtitle={`${locations.length} sub-locations`}
          />
          <StatCard
            icon={<IconCategory size={24} />}
            value={categories.length}
            label="Categories"
            color="cyan"
          />
          <StatCard
            icon={<IconReceipt size={24} />}
            value={audits.length}
            label="Audits"
            color="pink"
          />
        </SimpleGrid>

        {/* Charts Row 1 */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <AssetStatusChart assets={metrics.filteredAssets} />
          <AssetValueChart assets={metrics.filteredAssets} />
        </SimpleGrid>

        {/* Charts Row 2 */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <AssetsByCategoryChart assets={metrics.filteredAssets} />
          <Top5Widget metrics={metrics} />
        </SimpleGrid>

        {/* Audit Calendar */}
        <AuditCalendar audits={audits} />

        {/* Recent Activity and Assets */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <ActivityFeed limit={10} />
          <RecentAssets assets={metrics.filteredAssets} limit={10} />
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
